import type { GroupTraits, UserTraits } from '@segment/analytics-next';

import type { MixpanelTransportOptions } from '../config/mixpanel-config';
import { ANALYTICS_PLATFORM } from '../constants/analytics-context.constants';
import { ANALYTICS_TRANSPORT } from '../constants/analytics.constants';
import type { AnalyticsTransport } from '../transport/analytics-transport';
import { MixpanelTransport } from '../transport/mixpanel-transport';
import { SegmentTransport, type SegmentTransportOptions } from '../transport/segment-transport';
import type { TAnalyticsContext, TDeviceType } from '../types/analytics-context.types';
import type { TRecordedAnalyticsCall } from '../types/analytics.types';
import type { SensitiveFieldsConfig } from '../types/masking.types';
import type { TEventSchema, TEventsMap, TTaxonomyOptions } from '../types/taxonomy.types';
import { applyMasking } from '../utils/apply-masking';
import { omitUndefinedValues } from '../utils/omit-undefined-values';
import { pickDeclaredProperties } from '../utils/pick-declared-properties';
import { resolveDeviceType } from '../utils/resolve-device-type';

type TAnalyticsServiceSharedOptions<EventsMap extends TEventsMap> = {
  instanceName: string;
  enabled: boolean;
  mixpanel: Omit<MixpanelTransportOptions, 'instanceName'>;
  taxonomy?: TTaxonomyOptions<EventsMap>;
  sensitiveFields?: SensitiveFieldsConfig;
  recordCalls?: boolean;
  context?: TAnalyticsContext;
};

type TMixpanelTransportChoice = {
  transport: typeof ANALYTICS_TRANSPORT.MIXPANEL;
  segment?: SegmentTransportOptions;
};

type TSegmentTransportChoice = {
  transport: typeof ANALYTICS_TRANSPORT.SEGMENT;
  segment: SegmentTransportOptions;
};

export type AnalyticsServiceOptions<EventsMap extends TEventsMap = TEventsMap> = TAnalyticsServiceSharedOptions<EventsMap> &
  (TMixpanelTransportChoice | TSegmentTransportChoice);

export class AnalyticsService<EventsMap extends TEventsMap> {
  private readonly transport?: AnalyticsTransport;
  private readonly taxonomy?: TTaxonomyOptions<EventsMap>;
  private readonly sensitiveFields: SensitiveFieldsConfig;
  private readonly recordCalls: boolean;
  private readonly warnedDrops = new Set<string>();
  private deviceType?: TDeviceType;
  private context?: TAnalyticsContext;

  constructor({
    instanceName,
    enabled,
    transport,
    segment,
    mixpanel,
    taxonomy,
    sensitiveFields = {},
    recordCalls = false,
    context,
  }: AnalyticsServiceOptions<EventsMap>) {
    this.taxonomy = taxonomy;
    this.sensitiveFields = sensitiveFields;
    this.recordCalls = recordCalls;
    this.context = context && omitUndefinedValues(context);

    if (!enabled) return;

    try {
      this.transport =
        transport === ANALYTICS_TRANSPORT.MIXPANEL ? new MixpanelTransport({ ...mixpanel, instanceName }) : new SegmentTransport(segment);
    } catch (error) {
      this.reportFailure('constructor', error);
    }
  }

  identify(userId: string, traits?: UserTraits) {
    this.failOpen('identify', () => {
      if (!userId?.trim()) return;

      if (!this.taxonomy) {
        this.transport?.identify(userId, traits);
        this.record({ type: 'identify', userId });
        return;
      }

      const declaredTraits = this.pickDeclared('identify', traits, this.taxonomy.traits);

      this.transport?.identify(userId, declaredTraits);
      this.record({ type: 'identify', userId, ...(declaredTraits && { traits: declaredTraits }) });
    });
  }

  /** @deprecated Merchant association is the `merchant_id` context key. */
  group(groupId: string, traits?: GroupTraits) {
    this.failOpen('group', () => this.transport?.group(groupId, traits));
  }

  async getAnonymousId() {
    try {
      return this.transport ? await this.transport.getAnonymousId() : null;
    } catch (error) {
      this.reportFailure('getAnonymousId', error);
      return null;
    }
  }

  reset() {
    this.failOpen('reset', () => {
      this.transport?.reset();
      this.record({ type: 'reset' });
    });
  }

  setDeviceId(id: string) {
    this.failOpen('setDeviceId', () => {
      this.transport?.setDeviceId(id);
      this.record({ type: 'device', deviceId: id });
    });
  }

  startSessionReplay() {
    this.failOpen('startSessionReplay', () => {
      this.transport?.startSessionReplay();
      this.record({ type: 'session_replay', action: 'start' });
    });
  }

  stopSessionReplay() {
    this.failOpen('stopSessionReplay', () => {
      this.transport?.stopSessionReplay();
      this.record({ type: 'session_replay', action: 'stop' });
    });
  }

  setContext(partial: TAnalyticsContext) {
    this.context = omitUndefinedValues({ ...this.context, ...partial });
  }

  /** @deprecated Track a `<module>_page_viewed` event instead. */
  page(name: string, properties?: Record<string, unknown>) {
    this.failOpen(
      'page',
      () => {
        const pageProperties = this.taxonomy ? this.withContext() : applyMasking(this.withContext(properties), this.sensitiveFields);
        this.transport?.page(name, pageProperties);
        this.record({ type: 'page', name, properties: pageProperties ?? {} });
      },
      name,
    );
  }

  track<E extends keyof EventsMap>(eventName: E, properties?: EventsMap[E]) {
    const name = eventName as string;

    this.failOpen(
      'track',
      () => {
        const eventProperties = this.taxonomy
          ? this.toTaxonomyProperties(this.taxonomy, name, properties)
          : applyMasking(this.withContext(properties), this.sensitiveFields);

        if (eventProperties === null) return;

        this.transport?.track(name, eventProperties);
        this.record({ type: 'track', name, properties: eventProperties ?? {} });
      },
      name,
    );
  }

  private failOpen(method: string, run: () => void, eventName?: string) {
    try {
      run();
    } catch (error) {
      this.reportFailure(method, error, eventName);
    }
  }

  private reportFailure(method: string, error: unknown, eventName?: string) {
    const target = eventName ? `${method} "${eventName}"` : method;

    console.warn(`[analytics] ${target} failed: ${error instanceof Error ? error.message : String(error)}`);
  }

  private toTaxonomyProperties({ events }: TTaxonomyOptions<EventsMap>, name: string, properties?: Record<string, unknown>) {
    if (!Object.prototype.hasOwnProperty.call(events, name)) {
      this.warnDrop(name);
      return null;
    }

    return this.withContext(this.pickDeclared(name, properties, events[name]));
  }

  private pickDeclared(scope: string, properties: Record<string, unknown> | undefined, schema?: TEventSchema) {
    if (!properties) return undefined;

    const declared = schema ? pickDeclaredProperties(properties, schema) : { properties: {}, droppedKeys: Object.keys(properties) };
    declared.droppedKeys.forEach((key) => this.warnDrop(scope, key));

    return Object.keys(declared.properties).length > 0 ? declared.properties : undefined;
  }

  private warnDrop(scope: string, key?: string) {
    const dropId = key ? `${scope}.${key}` : scope;

    if (!this.taxonomy?.warnOnDrop || this.warnedDrops.has(dropId)) return;

    this.warnedDrops.add(dropId);
    console.warn(
      key ? `[analytics] Dropped undeclared or invalid property "${key}" on "${scope}"` : `[analytics] Dropped undeclared event "${scope}"`,
    );
  }

  private withContext(properties?: Record<string, unknown>) {
    if (!this.context) return properties;

    this.deviceType ??= resolveDeviceType(navigator.userAgent, navigator.maxTouchPoints);

    return { ...properties, ...this.context, platform: ANALYTICS_PLATFORM, device_type: this.deviceType };
  }

  private record(call: TRecordedAnalyticsCall) {
    if (!this.recordCalls) return;

    const tapWindow = window as unknown as { __glomoAnalyticsCalls?: Array<TRecordedAnalyticsCall> };
    tapWindow.__glomoAnalyticsCalls = [...(tapWindow.__glomoAnalyticsCalls ?? []), call];
  }
}
